import axios from '@/plugins/axios'
import { Module } from 'vuex'
import type { OrganizationAudience } from '@/views/account/strategy/organizationAudience'
import type { Perspective, TopicPerspectivesResponse } from '@/views/account/strategy/topicPerspectives'
import type { Pillar, PostMix, StrategyPlanResponse } from '@/views/account/strategy/strategyPlan'

// Member Settings > Audience Mapping. Each audience (the organization's, from Workspace Settings >
// Strategy > Audience Mapping) has a communication objective and content domains with angles: the member can reword them,
// switch domains off, and writes their approach. The API returns every audience, in order, with the
// framework's wording alongside (objectiveDefault, angleDefault), so a save returns the map as stored.
// draftAudience starts an AI draft of an audience's approach, which runs in the background
// (AudienceDraftRunner); getAudienceDrafts says how each audience's latest one went. The workspace
// audience actions are for admins adding their own audiences, which AI sets up in the background.

export type AudienceKey = string

// Content domains of the member's own an audience can have (AudienceMap::MAX_MEMBER_DOMAINS)
export const MAX_MEMBER_DOMAINS = 10
export type AudienceTone = 'casual' | 'conversational' | 'professional' | 'formal'
export type SetupStatus = 'queued' | 'running' | 'done' | 'failed'

export interface AudienceDomain {
    key: string
    name: string
    active: boolean
    angle: string
    angleDefault: string
    // Added by the member (Actions > Add content domain), not from the audience: no default wording
    member?: boolean
    // One of the audience's own the member deleted (it's off): left off their page. Only theirs
    deleted?: boolean
}

export interface Audience {
    key: AudienceKey
    name: string
    who: string
    builtIn: boolean
    id: number | null
    setupStatus: SetupStatus
    active: boolean
    // The member deleted it (it's off): left off their page until restored. Only theirs; other members keep it
    deleted?: boolean
    objective: string
    objectiveDefault: string
    approach: string
    tone: AudienceTone
    domains: AudienceDomain[]
}

// An audience the organization added that the AI suggests for the member's role
export interface AudienceSuggestion {
    key: AudienceKey
    reason: string
}

export interface AudienceMapResponse {
    audiences: Audience[]
    // Switched on from the member's role the first time the map was opened
    defaultsApplied: AudienceKey[]
    // Why the AI switched each audience on from their role
    roleReasons: Partial<Record<AudienceKey, string>>
    // Audiences the organization added since, that suit their role, until switched on or dismissed
    suggestions: AudienceSuggestion[]
    // Admins can change the organization's audiences
    canManageAudiences: boolean
}

export interface AudienceDraft {
    id: number
    audience: AudienceKey
    status: 'queued' | 'running' | 'done' | 'failed'
    filled: string[]
    error: string | null
}

// A workspace's own audience, as admins edit it
export interface WorkspaceAudience {
    key: AudienceKey
    id: number
    name: string
    who: string
    objective: string
    tone: AudienceTone
    domains: { key?: string; name: string; angle: string }[]
    setupStatus: SetupStatus
}

// Member Settings > Strategy > Objectives (GET /api/member-strategy/{memberId}/objectives)
export type ObjectivePriority = 'high' | 'medium' | 'low'

// One of the company's Business Objectives, as the member has it
export interface MemberBusinessObjective {
    id: number
    title: string
    goal: string
    enabled: boolean
    priority: ObjectivePriority
    companyPriority: ObjectivePriority
}

// One of the member's own objectives; supports: the company's objectives it serves
export interface PersonalObjective {
    id: number
    title: string
    goal: string
    supports: number[]
    priority: ObjectivePriority
    enabled: boolean
    isDefault: boolean
}

export interface MemberObjectivesResponse {
    business: MemberBusinessObjective[]
    personal: PersonalObjective[]
    // Why the AI switched each on from their role ("business-12", "personal-3")
    reasons: Record<string, string>
    // What the AI switched on just now, the first time
    picked: string[]
    // Past this many personal objectives on, the page suggests focusing
    nudgeAfter: number
}

// Member Settings > Strategy > Strategic Messaging (GET /api/member-strategy/{memberId}/messaging)
export interface MemberTheme {
    id: number
    name: string
    pointOfView: string
    topics: { id: number; name: string }[]
    enabled: boolean
}

export interface MemberMessagingResponse {
    // An expert who isn't an executive has the lighter Strategy: the company themes only
    isExecutive: boolean
    isExpert: boolean
    // What they want to be known for; byAi while it's Generate's, unedited
    narrative: { text: string; byAi: boolean }
    companyNarrative: string
    // The company's themes, each on or off for them; byAi while the switch is the AI's
    companyThemes: (MemberTheme & { priority: ObjectivePriority; byAi: boolean })[]
    personalThemes: MemberTheme[]
    // Why the AI switched each company theme on ("theme-12")
    reasons: Record<string, string>
    // What the AI switched on just now, the first time
    picked: string[]
    // The workspace's Topics, for their own themes
    topics: { id: number; name: string }[]
    maxPersonal: number
    // The latest Generate
    run: { status: 'queued' | 'running' | 'done' | 'failed'; error: string | null; requestedAt: string; finishedAt: string | null } | null
}

// Member Settings > Strategy > Expertise (GET /api/member-strategy/{memberId}/expertise)
export interface MemberExpertiseResponse {
    isExecutive: boolean
    isExpert: boolean
    // Their areas of expertise, from the workspace's Topics
    expertise: { id: number; name: string }[]
    // The workspace's Topics, to pick from
    topics: { id: number; name: string }[]
    // The company themes their expertise covers, the Topics that match, and whether it's on for them
    matches: { themeId: number; name: string; topics: string[]; enabled: boolean }[]
    // The signed-in member may mark them as an expert (workspace admins)
    canMark: boolean
}

// An executive's audiences (GET /api/member-strategy/{memberId}/audiences): the company's that are on for the
// company, enabled when they speak to it, and their own (own)
export interface MemberAudience extends OrganizationAudience {
    own: boolean
    // "org-{id}" or "own-{id}": its share and the strategy Summary refer to it by this
    key: string
    // The share of their posts it gets (while it's on); null until set
    share: number | null
    // One of the company's, as they have it: the company's wants and tone to go back to, and whether they
    // changed anything (wants, tone or content domains)
    companyWants?: string
    companyTone?: OrganizationAudience['tone']
    personalized?: boolean
}

export interface MemberAudiencesResponse {
    audiences: MemberAudience[]
}

// An executive's perspectives (GET /api/member-strategy/{memberId}/perspectives): every company perspective,
// carried when it's theirs (with their priority), and their own (own, always theirs)
export interface MemberPerspective extends Perspective {
    own: boolean
    carried: boolean
}

export interface MemberPerspectivesResponse extends Omit<TopicPerspectivesResponse, 'perspectives'> {
    perspectives: MemberPerspective[]
}

const fresh = { cache: false }

const audienceMapModule: Module<any, any> = {
    actions: {
        async getAudienceMap(_, memberId: number): Promise<AudienceMapResponse> {
            const { data } = await axios.get(`/api/audience-maps/${memberId}`, fresh)
            return {
                audiences: data.audiences,
                defaultsApplied: data.defaultsApplied ?? [],
                roleReasons: data.roleReasons ?? {},
                suggestions: data.suggestions ?? [],
                canManageAudiences: !!data.canManageAudiences,
            }
        },

        // An audience suggested for the member's role: dismissed, it stays off and isn't suggested again
        async dismissAudienceSuggestion(_, { memberId, key }: { memberId: number; key: AudienceKey }): Promise<void> {
            await axios.post(`/api/audience-maps/${memberId}/suggestions/${key}/dismiss`, {}, { silent: true })
        },

        // Start an AI draft of one audience's approach (or get the one already under way); the API
        // saves what it fills. Silent: the page says what went wrong.
        // mode "angles": Generate, which rewrites the audience's angles for the executive and company
        async draftAudience(_, { memberId, audience, mode }: { memberId: number; audience: AudienceKey; mode?: 'approach' | 'angles' }): Promise<AudienceDraft> {
            const { data } = await axios.post(`/api/audience-maps/${memberId}/draft`, mode ? { audience, mode } : { audience }, { silent: true })
            return data
        },

        // Each audience's latest AI draft, by audience
        async getAudienceDrafts(_, memberId: number): Promise<Partial<Record<AudienceKey, AudienceDraft>>> {
            const { data } = await axios.get(`/api/audience-maps/${memberId}/drafts`, { ...fresh, silent: true })
            return data.drafts ?? {}
        },

        // Each audience's latest Generate
        async getAudienceAngleDrafts(_, memberId: number): Promise<Partial<Record<AudienceKey, AudienceDraft>>> {
            const { data } = await axios.get(`/api/audience-maps/${memberId}/drafts`, { ...fresh, silent: true })
            return data.angles ?? {}
        },

        async updateAudienceMap(_, { memberId, audiences }: { memberId: number; audiences: Audience[] }): Promise<Audience[]> {
            const { data } = await axios.put(`/api/audience-maps/${memberId}`, { audiences })
            return data.audiences
        },

        async getMemberObjectives(_, memberId: number): Promise<MemberObjectivesResponse> {
            const { data } = await axios.get(`/api/member-strategy/${memberId}/objectives`, fresh)
            return data
        },

        // { enabled } and/or { priority }; priority null follows the company's
        async setMemberBusinessObjective(_, { memberId, id, ...changes }: { memberId: number; id: number; enabled?: boolean; priority?: ObjectivePriority | null }): Promise<MemberObjectivesResponse> {
            const { data } = await axios.put(`/api/member-strategy/${memberId}/objectives/business/${id}`, changes, { silent: true })
            return data
        },

        // { title, goal, supports, priority }
        async addPersonalObjective(_, { memberId, ...params }: { memberId: number } & Partial<PersonalObjective>): Promise<MemberObjectivesResponse> {
            const { data } = await axios.post(`/api/member-strategy/${memberId}/objectives/personal`, params, { silent: true })
            return data
        },

        async editPersonalObjective(_, { memberId, id, ...changes }: { memberId: number; id: number } & Partial<PersonalObjective>): Promise<MemberObjectivesResponse> {
            const { data } = await axios.put(`/api/member-strategy/${memberId}/objectives/personal/${id}`, changes, { silent: true })
            return data
        },

        async deletePersonalObjective(_, { memberId, id }: { memberId: number; id: number }): Promise<MemberObjectivesResponse> {
            const { data } = await axios.delete(`/api/member-strategy/${memberId}/objectives/personal/${id}`, { silent: true })
            return data
        },

        async getMemberMessaging(_, memberId: number): Promise<MemberMessagingResponse> {
            const { data } = await axios.get(`/api/member-strategy/${memberId}/messaging`, fresh)
            return data
        },

        async saveMemberNarrative(_, { memberId, text }: { memberId: number; text: string }): Promise<MemberMessagingResponse> {
            const { data } = await axios.put(`/api/member-strategy/${memberId}/messaging/narrative`, { text }, { silent: true })
            return data
        },

        // One of the company's themes, on or off for the member
        async setMemberTheme(_, { memberId, id, enabled }: { memberId: number; id: number; enabled: boolean }): Promise<MemberMessagingResponse> {
            const { data } = await axios.put(`/api/member-strategy/${memberId}/messaging/themes/${id}`, { enabled }, { silent: true })
            return data
        },

        // { name, pointOfView, topicIds }
        async addPersonalTheme(_, { memberId, ...params }: { memberId: number; name: string; pointOfView?: string; topicIds?: number[] }): Promise<MemberMessagingResponse> {
            const { data } = await axios.post(`/api/member-strategy/${memberId}/messaging/personal`, params, { silent: true })
            return data
        },

        async editPersonalTheme(_, { memberId, id, ...changes }: { memberId: number; id: number; name?: string; pointOfView?: string; topicIds?: number[]; enabled?: boolean }): Promise<MemberMessagingResponse> {
            const { data } = await axios.put(`/api/member-strategy/${memberId}/messaging/personal/${id}`, changes, { silent: true })
            return data
        },

        async deletePersonalTheme(_, { memberId, id }: { memberId: number; id: number }): Promise<MemberMessagingResponse> {
            const { data } = await axios.delete(`/api/member-strategy/${memberId}/messaging/personal/${id}`, { silent: true })
            return data
        },

        // Generate: queued, it runs in the background (the response's run says how it's going)
        async generateMemberMessaging(_, memberId: number): Promise<MemberMessagingResponse> {
            const { data } = await axios.post(`/api/member-strategy/${memberId}/messaging/generate`, {}, { silent: true })
            return data
        },

        async getMemberExpertise(_, memberId: number): Promise<MemberExpertiseResponse> {
            const { data } = await axios.get(`/api/member-strategy/${memberId}/expertise`, { ...fresh, silent: true })
            return data
        },

        // { isExpert } (admins) and/or { topicIds }
        async updateMemberExpertise(_, { memberId, ...changes }: { memberId: number; isExpert?: boolean; topicIds?: number[] }): Promise<MemberExpertiseResponse> {
            const { data } = await axios.put(`/api/member-strategy/${memberId}/expertise`, changes, { silent: true })
            return data
        },

        async getWorkspaceAudiences(): Promise<WorkspaceAudience[]> {
            const { data } = await axios.get('/api/workspace-audiences', { ...fresh, silent: true })
            return data.audiences ?? []
        },

        // Silent: the popup shows the API's reason (e.g. a name that's taken)
        async createWorkspaceAudience(_, { name, who }: { name: string; who: string }): Promise<WorkspaceAudience> {
            const { data } = await axios.post('/api/workspace-audiences', { name, who }, { silent: true })
            return data
        },

        async updateWorkspaceAudience(_, { id, ...changes }: Partial<WorkspaceAudience> & { id: number }): Promise<WorkspaceAudience> {
            const { data } = await axios.put(`/api/workspace-audiences/${id}`, changes, { silent: true })
            return data
        },

        async setUpWorkspaceAudience(_, id: number): Promise<WorkspaceAudience> {
            const { data } = await axios.post(`/api/workspace-audiences/${id}/setup`, {}, { silent: true })
            return data
        },

        async deleteWorkspaceAudience(_, id: number): Promise<void> {
            await axios.delete(`/api/workspace-audiences/${id}`, { silent: true })
        },

        // An executive's Strategy (Member Settings > Strategy): the company's three sections, each with
        // the company's items switched on or off for them, and their own (`own`). Business Objectives
        // use the objectives actions above.

        // Audiences: the company's (switched on: they speak to it) and their own
        async getMemberAudiences(_, memberId: number): Promise<MemberAudiencesResponse> {
            const { data } = await axios.get(`/api/member-strategy/${memberId}/audiences`, fresh)
            return data
        },

        // A company audience: { enabled }, and/or their own version of it: { wants, tone, domains } or { reset: true }.
        // Their own: any of its fields
        async editMemberAudience(_, { memberId, id, own, ...changes }: { memberId: number; id: number; own: boolean } & Partial<MemberAudience>): Promise<MemberAudiencesResponse> {
            const { data } = await axios.put(`/api/member-strategy/${memberId}/audiences/${own ? 'own' : 'company'}/${id}`, changes, { silent: true })
            return data
        },

        async addMemberAudience(_, { memberId, ...params }: { memberId: number } & Partial<MemberAudience>): Promise<MemberAudiencesResponse> {
            const { data } = await axios.post(`/api/member-strategy/${memberId}/audiences/own`, params, { silent: true })
            return data
        },

        async deleteMemberAudience(_, { memberId, id }: { memberId: number; id: number }): Promise<MemberAudiencesResponse> {
            const { data } = await axios.delete(`/api/member-strategy/${memberId}/audiences/own/${id}`, { silent: true })
            return data
        },

        // Each audience they speak to's share of their posts, by key, adding up to 100. Returns the Summary
        async setMemberAudienceShares(_, { memberId, shares }: { memberId: number; shares: Record<string, number> }): Promise<StrategyPlanResponse> {
            const { data } = await axios.put(`/api/member-strategy/${memberId}/audiences/shares`, { shares }, { silent: true })
            return data
        },

        // Summary: their strategy on one page (plan: null until the first Generate)
        async getMemberStrategyPlan(_, memberId: number): Promise<StrategyPlanResponse> {
            const { data } = await axios.get(`/api/member-strategy/${memberId}/plan`, { ...fresh, silent: true })
            return data
        },

        // Write it with AI, in the background: the response's run says how it's going
        async generateMemberStrategyPlan(_, memberId: number): Promise<StrategyPlanResponse> {
            const { data } = await axios.post(`/api/member-strategy/${memberId}/plan/generate`, {}, { silent: true })
            return data
        },

        // Any of the post mix (adding up to 100), measure targets (by measure key), the pillars (the whole list)
        // and following the Topics to follow (by key)
        async updateMemberStrategyPlan(
            _,
            {
                memberId,
                ...changes
            }: {
                memberId: number
                mix?: Omit<PostMix, 'edited'>
                targets?: Record<string, string>
                pillars?: Pillar[]
                follow?: Record<string, boolean>
            },
        ): Promise<StrategyPlanResponse> {
            const { data } = await axios.put(`/api/member-strategy/${memberId}/plan`, changes, { silent: true })
            return data
        },

        // Perspectives: the company's (carried: switched on for them) and their own
        async getMemberPerspectives(_, memberId: number): Promise<MemberPerspectivesResponse> {
            const { data } = await axios.get(`/api/member-strategy/${memberId}/perspectives`, fresh)
            return data
        },

        // A company perspective: { carried } (added to theirs, or removed) and/or their own wording of it
        // ({ text, proofPoints, priority }: the company's stays as it is)
        async setMemberPerspective(_, { memberId, id, ...changes }: { memberId: number; id: number; carried?: boolean; text?: string; proofPoints?: string[]; priority?: string }): Promise<MemberPerspectivesResponse> {
            const { data } = await axios.put(`/api/member-strategy/${memberId}/perspectives/company/${id}`, changes, { silent: true })
            return data
        },

        // { topicId, text, proofPoints, priority }
        async addMemberPerspective(_, { memberId, ...params }: { memberId: number } & Record<string, unknown>): Promise<MemberPerspectivesResponse> {
            const { data } = await axios.post(`/api/member-strategy/${memberId}/perspectives/own`, params, { silent: true })
            return data
        },

        async editMemberPerspective(_, { memberId, id, ...changes }: { memberId: number; id: number } & Record<string, unknown>): Promise<MemberPerspectivesResponse> {
            const { data } = await axios.put(`/api/member-strategy/${memberId}/perspectives/own/${id}`, changes, { silent: true })
            return data
        },

        async deleteMemberPerspective(_, { memberId, id }: { memberId: number; id: number }): Promise<MemberPerspectivesResponse> {
            const { data } = await axios.delete(`/api/member-strategy/${memberId}/perspectives/own/${id}`, { silent: true })
            return data
        },

        // Generate Perspective and Find Proof, written for them: { topicId, text }. Return { text } / { proofPoints }
        async draftMemberPerspective(_, { memberId, ...params }: { memberId: number } & Record<string, unknown>): Promise<{ text: string }> {
            const { data } = await axios.post(`/api/member-strategy/${memberId}/perspectives/draft`, params, { silent: true })
            return data
        },

        async findMemberPerspectiveProof(_, { memberId, ...params }: { memberId: number } & Record<string, unknown>): Promise<{ proofPoints: string[] }> {
            const { data } = await axios.post(`/api/member-strategy/${memberId}/perspectives/proof`, params, { silent: true })
            return data
        },

        // The Strategy checklist for them: as GET /api/strategy/progress, for their three sections
        async getMemberStrategyProgress(_, memberId: number) {
            const { data } = await axios.get(`/api/member-strategy/${memberId}/progress`, { cache: false, silent: true })
            return data
        },
    },
}

export default audienceMapModule
